package uk.co.nickthecoder.kyd.util

import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.kyd.radiansToDegrees
import kotlin.math.*

data class Vector2(val x: Float, val y: Float) {

    fun magnitude() = sqrt(x * x + y * y)

    operator fun unaryMinus() = Vector2(- x, - y)

    operator fun plus(other: Vector2) = Vector2(x + other.x, y + other.y)

    fun plus(x2: Float, y2: Float) = Vector2(x + x2, y + y2)

    operator fun minus(other: Vector2) = Vector2(x - other.x, y - other.y)

    fun minus(x2: Float, y2: Float) = Vector2(x - x2, y - y2)

    operator fun times(value: Vector2): Vector2 {
        return Vector2(x * value.x, y * value.y)
    }

    operator fun times(value: Float): Vector2 {
        return Vector2(x * value, y * value)
    }

    operator fun times(matrix: Matrix): Vector2 {
        return Vector2(
            x * matrix.m00 + y * matrix.m10 + matrix.m20,
            x * matrix.m01 + y * matrix.m11 + matrix.m21
        )
    }
    operator fun div(value: Vector2): Vector2 {
        return Vector2(x / value.x, y / value.y)
    }

    operator fun div(value: Float): Vector2 {
        return Vector2(x / value, y / value)
    }


    fun dot(other: Vector2) = x * other.x + y * other.y
    fun cross(other: Vector2) = x * other.y - y * other.x
    fun perpendicular() = Vector2(- y, x)

    /**
     * A vector in the same direction, but with a magnitude of 1.
     */
    fun direction() = this / magnitude()

    /**
     * A vector at right angles to this, with a magnitude of 1.
     */
    fun normal() = perpendicular().direction()

    fun angleRadians(): Float = atan2(y, x)
    fun angleDegrees(): Float = angleRadians() * radiansToDegrees

    fun angleRadians(other: Vector2): Float = acos((this.dot(other) / this.magnitude() / other.magnitude()))
    fun angleDegrees(other: Vector2): Float =
        (acos((this.dot(other) * radiansToDegrees / this.magnitude() / other.magnitude())))

    fun rotateRadians(radians: Double): Vector2 {
        val sin = sin(radians)
        val cos = cos(radians)
        return Vector2(
            (cos * x - sin * y).toFloat(),
            (sin * x + cos * y).toFloat()
        )
    }

    fun rotateRadians(radians: Float): Vector2 {
        val sin = sin(radians)
        val cos = cos(radians)
        return Vector2(
            (cos * x - sin * y),
            (sin * x + cos * y)
        )
    }

    override fun toString() = "( $x , $y )"

    companion object {
        val ZERO = Vector2(0f, 0f)
        val ONE = Vector2(1f, 1f)

        fun parse(str: String): Vector2 {
            val parts = str.replace("(", "")
                .replace(")", "")
                .split(",")
            if (parts.size != 2) {
                throw IllegalArgumentException("Not a Vector2 : '$str'")
            }
            return Vector2(parts[0].toFloat(), parts[1].toFloat())
        }
    }

}
